import { useState, useEffect, useCallback } from "react";

// Lightweight geolocation hook + nearby/all toggle.
// Caches last known coords in localStorage so subsequent page loads default to
// "nearby" without re-prompting the user. The toggle is also persisted per app
// so the explicit choice ("show all India") sticks across sessions.

const COORDS_KEY = "petbond_geo_coords";
const MODE_KEY = "petbond_geo_mode"; // "nearby" | "all"

function loadCoords() {
  try {
    const raw = localStorage.getItem(COORDS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.lat === "number" && typeof p?.lng === "number") return p;
  } catch {}
  return null;
}

function loadMode() {
  return localStorage.getItem(MODE_KEY) === "all" ? "all" : "nearby";
}

export default function useGeo(defaultRadiusKm = 25) {
  const [coords, setCoords] = useState(loadCoords());
  const [mode, setModeState] = useState(loadMode());
  const [radius, setRadius] = useState(defaultRadiusKm);
  const [error, setError] = useState(null);
  const [requesting, setRequesting] = useState(false);

  const setMode = useCallback((m) => {
    localStorage.setItem(MODE_KEY, m);
    setModeState(m);
  }, []);

  const request = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported on this device.");
      return;
    }
    setRequesting(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        localStorage.setItem(COORDS_KEY, JSON.stringify(c));
        setCoords(c);
        setMode("nearby");
        setRequesting(false);
      },
      (err) => {
        setError(err?.message || "Location permission denied.");
        setRequesting(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  }, [setMode]);

  // Auto-request on mount only if we have no cached coords AND user is in nearby mode
  useEffect(() => {
    if (!coords && mode === "nearby") {
      request();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Returns the axios params object to merge into requests
  const geoParams = mode === "nearby" && coords
    ? { lat: coords.lat, lng: coords.lng, radius_km: radius }
    : {};

  return {
    coords,
    mode,
    setMode,
    radius,
    setRadius,
    error,
    requesting,
    request,
    geoParams,
    isNearby: mode === "nearby" && !!coords,
  };
}
